import { Injectable } from '@angular/core';
import { FileHandlerService } from './file-handler.service';
import { IKBArtifact } from '../interfaces/IBase-knowledge-catalog';

export interface FileValidationResult {
  validFiles: File[];
  invalidFiles: { file: File; isValid: boolean; error: string }[];
  hasMaxFilesError?: boolean;
  maxFilesErrorMessage?: string;
}

@Injectable({
  providedIn: 'root'
})
export class FileValidationService {
  private static readonly MAX_FILES_ALLOWED = 7;

  constructor(private _fileHandlerService: FileHandlerService) { }

  validateUploadedFiles(files: File[], existingArtifacts: IKBArtifact[] = []): FileValidationResult {
    // Validar si el total de archivos supera el máximo permitido
    const maxFilesValidation = this.validateMaxFilesLimit(files, existingArtifacts);
    if (maxFilesValidation) {
      return maxFilesValidation;
    }

    // Validaciones individuales por archivo (incluyendo longitud del nombre)
    const validationResults = this._fileHandlerService.validateFiles(files);
    const invalidFiles = validationResults
      .filter(result => !result.isValid)
      .map(result => ({
        file: result.file,
        isValid: result.isValid,
        error: result.error || 'BASEKNOWLEDGECATALOG.ERRORS.ERROR_READING_FILE'
      }));
    const validFiles = validationResults.filter(result => result.isValid).map(result => result.file);
    
    // Verificar duplicados en los archivos válidos
    const duplicateFiles = this.checkForDuplicateFiles(validFiles, existingArtifacts);
    invalidFiles.push(...duplicateFiles);

    // Filtrar archivos válidos para eliminar duplicados
    const validFilesNoDuplicates = validFiles.filter(file => 
      !duplicateFiles.some(dupFile => dupFile.file.name === file.name)
    );
    
    return { 
      validFiles: validFilesNoDuplicates, 
      invalidFiles 
    };
  }

  private validateMaxFilesLimit(files: File[], existingArtifacts: IKBArtifact[] = []): FileValidationResult | null {
    const activeExistingArtifacts = existingArtifacts.filter(artifact => !artifact.Deleted);
    const totalFilesAfterUpload = activeExistingArtifacts.length + files.length;
    if (totalFilesAfterUpload > FileValidationService.MAX_FILES_ALLOWED) {
      return { 
        validFiles: [], 
        invalidFiles: [],
        hasMaxFilesError: true,
        maxFilesErrorMessage: `BASEKNOWLEDGECATALOG.ERRORS.MAX_FILES_EXCEEDED|${FileValidationService.MAX_FILES_ALLOWED}`
      };
    }
    return null;
  }

  private checkForDuplicateFiles(
    files: File[], 
    existingArtifacts: IKBArtifact[] = []
  ): { file: File; isValid: boolean; error: string }[] {
    const duplicateFiles: { file: File; isValid: boolean; error: string }[] = [];
    const processedFileNames = new Set<string>();
    
    files.forEach(file => {
      const isDuplicateInArtifacts = existingArtifacts.some(artifact => 
        artifact.FileName === file.name && !artifact.Deleted
      );
      
      const isDuplicateInSelection = processedFileNames.has(file.name);
      
      if (isDuplicateInArtifacts || isDuplicateInSelection) {
        duplicateFiles.push({
          file: file,
          isValid: false,
          error: `BASEKNOWLEDGECATALOG.ERRORS.FILE_ALREADY_EXISTS|${file.name}`
        });
      } else {
        processedFileNames.add(file.name);
      }
    });
    
    return duplicateFiles;
  }

  isFileDuplicate(fileName: string, existingArtifacts: IKBArtifact[] = []): boolean {
    return existingArtifacts.some(artifact => artifact.FileName === fileName && !artifact.Deleted);
  }

  getValidationSummary(validationResult: FileValidationResult): { 
    totalFiles: number; 
    validCount: number; 
    invalidCount: number; 
  } {
    const totalFiles = validationResult.validFiles.length + validationResult.invalidFiles.length;
    return {
      totalFiles,
      validCount: validationResult.validFiles.length,
      invalidCount: validationResult.invalidFiles.length
    };
  }

  groupInvalidFilesByError(invalidFiles: { file: File; isValid: boolean; error: string }[]): {
    [errorType: string]: { file: File; isValid: boolean; error: string }[]
  } {
    return invalidFiles.reduce((groups, invalidFile) => {
      const errorKey = invalidFile.error.split('|')[0];
      if (!groups[errorKey]) {
        groups[errorKey] = [];
      }
      groups[errorKey].push(invalidFile);
      return groups;
    }, {} as { [errorType: string]: { file: File; isValid: boolean; error: string }[] });
  }
}
